(() => {
  const DESKTOP_QUERY = '(min-width: 1180px)';

  class MiniPowerHeader {
    constructor(root) {
      if (root.dataset.mpHeaderReady === 'true') return;
      root.dataset.mpHeaderReady = 'true';
      this.root = root;
      this.desktop = window.matchMedia(DESKTOP_QUERY);
      this.nav = root.querySelector('.mp-header__nav');
      this.navList = root.querySelector('.mp-header__nav-list');
      this.navItems = [...root.querySelectorAll('[data-mp-nav-item]')];
      this.drawer = root.querySelector('[data-mp-mobile-drawer]');
      this.drawerHome = this.drawer?.parentElement || null;
      this.menuTrigger = root.querySelector('[data-mp-menu-trigger]');
      this.mobileList = this.drawer?.querySelector('[data-mp-mobile-list]') || null;
      this.mobileItems = this.drawer
        ? [...this.drawer.querySelectorAll('[data-mp-mobile-item]')]
        : [];
      this.lastFocused = null;
      this.activeItem = null;
      this.activeMobileItem = null;
      this.openTimer = null;
      this.closeTimer = null;
      this.mountDrawer();
      this.bindDesktopMenu();
      this.bindMobileMenu();
      this.bindGlobalEvents();
      this.measure();
    }

    mountDrawer() {
      if (this.drawer && this.drawer.parentElement !== document.body) {
        document.body.append(this.drawer);
      }
    }

    restoreDrawer() {
      if (this.drawer && this.drawerHome && this.drawer.parentElement !== this.drawerHome) {
        this.drawerHome.append(this.drawer);
      }
    }

    bindDesktopMenu() {
      this.nav?.addEventListener('pointerenter', (event) => {
        if (!this.desktop.matches || event.pointerType !== 'mouse') return;
        window.clearTimeout(this.closeTimer);
      });
      this.nav?.addEventListener('pointerleave', (event) => {
        if (!this.desktop.matches || event.pointerType !== 'mouse' || this.nav.contains(event.relatedTarget)) return;
        window.clearTimeout(this.openTimer);
        window.clearTimeout(this.closeTimer);
        this.closeTimer = window.setTimeout(() => this.closeAllDesktop(), 420);
      });

      this.navItems.forEach((item) => {
        const toggle = item.querySelector('[data-mp-nav-toggle]');
        if (!toggle) return;
        item.addEventListener('pointerenter', (event) => {
          if (!this.desktop.matches || event.pointerType !== 'mouse') return;
          window.clearTimeout(this.openTimer);
          window.clearTimeout(this.closeTimer);
          const delay = this.activeItem && this.activeItem !== item ? 320 : 180;
          this.openTimer = window.setTimeout(() => {
            if (item.matches(':hover')) this.openDesktop(item);
          }, delay);
        });
        item.addEventListener('focusout', (event) => {
          if (!this.desktop.matches || this.nav?.contains(event.relatedTarget)) return;
          this.closeAllDesktop();
        });
        toggle.addEventListener('click', (event) => {
          event.preventDefault();
          window.clearTimeout(this.openTimer);
          window.clearTimeout(this.closeTimer);
          const isOpen = toggle.getAttribute('aria-expanded') === 'true';
          if (isOpen) this.closeDesktop(item);
          else this.openDesktop(item);
        });
      });
    }

    openDesktop(item) {
      this.navItems.forEach((candidate) => {
        if (candidate !== item) this.closeDesktop(candidate);
      });
      const toggle = item.querySelector('[data-mp-nav-toggle]');
      const panel = item.querySelector('[data-mp-mega]');
      if (!toggle || !panel) return;
      item.classList.add('is-open');
      this.navList?.classList.add('has-selection');
      toggle.setAttribute('aria-expanded', 'true');
      panel.hidden = false;
      this.activeItem = item;
    }

    closeDesktop(item) {
      const toggle = item.querySelector('[data-mp-nav-toggle]');
      const panel = item.querySelector('[data-mp-mega]');
      item.classList.remove('is-open');
      if (toggle) toggle.setAttribute('aria-expanded', 'false');
      if (panel) panel.hidden = true;
      if (this.activeItem === item) this.activeItem = null;
      if (!this.navItems.some((candidate) => candidate.classList.contains('is-open'))) {
        this.navList?.classList.remove('has-selection');
      }
    }

    closeAllDesktop() {
      window.clearTimeout(this.openTimer);
      window.clearTimeout(this.closeTimer);
      this.navItems.forEach((item) => this.closeDesktop(item));
    }

    bindMobileMenu() {
      if (!this.drawer || !this.menuTrigger) return;
      this.menuTrigger.addEventListener('click', () => this.openDrawer());
      this.drawer.querySelectorAll('[data-mp-menu-close]').forEach((button) => {
        button.addEventListener('click', () => this.closeDrawer());
      });
      this.mobileItems.forEach((item) => {
        const details = item.querySelector('[data-mp-mobile-details]');
        if (!details) return;
        details.addEventListener('toggle', () => {
          item.classList.toggle('is-open', details.open);
          this.syncMobileSelection(details.open ? item : null);
        });
      });
    }

    syncMobileSelection(preferredItem = null) {
      const selectedItem = preferredItem?.querySelector('[data-mp-mobile-details]')?.open
        ? preferredItem
        : [...this.mobileItems]
            .reverse()
            .find((candidate) => candidate.querySelector('[data-mp-mobile-details]')?.open) || null;
      this.mobileItems.forEach((item) => item.classList.toggle('is-selected', item === selectedItem));
      this.mobileList?.classList.toggle('has-selection', Boolean(selectedItem));
      this.activeMobileItem = selectedItem;
    }

    closeMobileSubmenu(restoreFocus = false) {
      const previousItem = this.activeMobileItem;
      if (!previousItem) return;
      const details = previousItem.querySelector('[data-mp-mobile-details]');
      if (details) details.open = false;
      previousItem.classList.remove('is-open');
      this.syncMobileSelection();
      if (restoreFocus) {
        const toggle = previousItem.querySelector('[data-mp-mobile-toggle]');
        window.requestAnimationFrame(() => toggle?.focus({ preventScroll: true }));
      }
    }

    resetMobileMenu() {
      this.mobileItems.forEach((item) => {
        const details = item.querySelector('[data-mp-mobile-details]');
        if (details) details.open = false;
        item.classList.remove('is-open', 'is-selected');
      });
      this.activeMobileItem = null;
      this.mobileList?.classList.remove('has-selection');
      const panel = this.drawer?.querySelector('.mp-header__drawer-panel');
      if (panel) panel.scrollTop = 0;
    }

    openDrawer() {
      if (!this.drawer || !this.menuTrigger) return;
      this.resetMobileMenu();
      this.lastFocused = document.activeElement;
      this.drawer.hidden = false;
      this.menuTrigger.setAttribute('aria-expanded', 'true');
      document.body.classList.add('mp-header-drawer-open');
      const focusTarget = this.drawer.querySelector('[data-mp-menu-close]:not([tabindex="-1"])');
      window.requestAnimationFrame(() => focusTarget?.focus());
    }

    closeDrawer(restoreFocus = true) {
      if (!this.drawer || this.drawer.hidden) return;
      this.resetMobileMenu();
      this.drawer.hidden = true;
      this.menuTrigger?.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('mp-header-drawer-open');
      if (restoreFocus && this.lastFocused && typeof this.lastFocused.focus === 'function') {
        this.lastFocused.focus({ preventScroll: true });
      }
    }

    bindGlobalEvents() {
      document.addEventListener('click', (event) => {
        if (this.desktop.matches && !this.root.contains(event.target)) this.closeAllDesktop();
      });
      document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        if (this.drawer && !this.drawer.hidden) {
          event.preventDefault();
          if (this.activeMobileItem) this.closeMobileSubmenu(true);
          else this.closeDrawer();
          return;
        }
        const openToggle = this.root.querySelector('[data-mp-nav-toggle][aria-expanded="true"]');
        if (openToggle) {
          event.preventDefault();
          this.closeAllDesktop();
          openToggle.focus();
        }
      });
      this.desktop.addEventListener('change', () => {
        this.closeAllDesktop();
        if (this.desktop.matches) this.closeDrawer(false);
        this.measure();
      });
      window.addEventListener('resize', () => this.measure(), { passive: true });
      document.addEventListener('shopify:section:unload', (event) => {
        if (!event.target.contains(this.root)) return;
        this.restoreDrawer();
      });
    }

    measure() {
      const section = this.root.closest('.t4s-section-header');
      if (!section) return;
      document.documentElement.style.setProperty('--header-height', `${Math.round(section.offsetHeight)}px`);
    }
  }

  const init = (scope = document) => {
    scope.querySelectorAll('[data-mp-header]').forEach((root) => new MiniPowerHeader(root));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
